import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faVolumeUp, faVolumeMute, faDownload } from '@fortawesome/free-solid-svg-icons';

// Helper to normalize paths
const normalizePath = (path) => {
  if (!path) return path;
  if (path.startsWith('/') || path.startsWith('http')) return path;
  return `${process.env.PUBLIC_URL}/${path}`;
};

function AudioPlayer({ src, title, downloadUrl, mimeType = 'audio/mpeg' }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Normalize paths
  const normalizedSrc = normalizePath(src);
  const normalizedDownloadUrl = normalizePath(downloadUrl);

  // Format time as MM:SS
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  // Handle progress bar click
  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    const newTime = percentage * audioDuration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle volume toggle
  const toggleMute = () => {
    if (!audioRef.current) return;

    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      setVolume(audioRef.current.volume); // Save current volume before muting
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  // Handle audio end
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Calculate progress percentage
  const progressPercent = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  // Generate waveform bars (decorative)
  const waveformBars = Array.from({ length: 40 }, (_, i) => {
    const height = Math.sin((i / 40) * Math.PI * 2 + currentTime) * 0.3 + 0.5;
    const isActive = (i / 40) * 100 <= progressPercent;
    return (
      <div
        key={i}
        className="waveform-bar"
        style={{
          height: `${height * 100}%`,
          backgroundColor: isActive ? 'var(--primary)' : 'var(--border)',
          opacity: isActive ? 1 : 0.4,
        }}
      />
    );
  });

  return (
    <div className="audio-player-modern">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        <source src={normalizedSrc} type={mimeType} />
        Your browser does not support the audio element.
      </audio>

      <div className="audio-player-container">
        {/* Play/Pause Button */}
        <button
          className={`play-button ${isPlaying ? 'playing' : ''}`}
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          <div className="play-button-inner">
            <FontAwesomeIcon
              icon={isPlaying ? faPause : faPlay}
              className={`play-icon ${isPlaying ? '' : 'play-offset'}`}
            />
          </div>
          {/* Animated rings when playing */}
          {isPlaying && (
            <>
              <div className="pulse-ring ring-1" />
              <div className="pulse-ring ring-2" />
            </>
          )}
        </button>

        {/* Main content */}
        <div className="audio-content">
          {/* Title and time */}
          <div className="audio-info">
            <span className="audio-title">{title}</span>
            <span className="audio-time">
              {formatTime(currentTime)} / {formatTime(audioDuration || 0)}
            </span>
          </div>

          {/* Progress bar with waveform */}
          <div
            className="progress-container"
            ref={progressRef}
            onClick={handleProgressClick}
          >
            {/* Waveform visualization */}
            <div className="waveform-container">
              {waveformBars}
            </div>

            {/* Progress overlay */}
            <div
              className="progress-bar"
              style={{ width: `${progressPercent}%` }}
            />

            {/* Scrubber handle */}
            <div
              className="scrubber-handle"
              style={{ left: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="audio-controls">
          {/* Volume */}
          <button
            className="control-button"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
          </button>

          {/* Download */}
          {normalizedDownloadUrl && (
            <a
              href={normalizedDownloadUrl}
              download
              className="control-button"
              aria-label="Download audio"
            >
              <FontAwesomeIcon icon={faDownload} />
            </a>
          )}
        </div>
      </div>

      <style>{`
        .audio-player-modern {
          width: 100%;
        }

        .audio-player-container {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 16px;
          background: linear-gradient(135deg,
            rgba(59, 130, 246, 0.08) 0%,
            rgba(139, 92, 246, 0.08) 100%
          );
          border-radius: 16px;
          border: 1px solid var(--border);
          backdrop-filter: blur(10px);
        }

        .play-button {
          position: relative;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          background: linear-gradient(135deg, var(--primary) 0%, #8b5cf6 100%);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
        }

        .play-button:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
        }

        .play-button:active {
          transform: scale(0.95);
        }

        .play-button-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }

        .play-icon {
          color: white;
          font-size: 16px;
          transition: transform 0.2s ease;
        }

        .play-offset {
          margin-left: 3px;
        }

        .pulse-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 2px solid var(--primary);
          opacity: 0;
          animation: pulse-ring 1.5s ease-out infinite;
        }

        .ring-1 {
          animation-delay: 0s;
        }

        .ring-2 {
          animation-delay: 0.5s;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        .audio-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .audio-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .audio-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .audio-time {
          font-size: 0.75rem;
          color: var(--text-light);
          font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
          white-space: nowrap;
        }

        .progress-container {
          position: relative;
          height: 32px;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
          cursor: pointer;
          overflow: hidden;
        }

        .dark .progress-container {
          background: rgba(255, 255, 255, 0.05);
        }

        .waveform-container {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 8px;
          gap: 2px;
        }

        .waveform-bar {
          flex: 1;
          min-width: 2px;
          max-width: 4px;
          border-radius: 2px;
          transition: height 0.1s ease, background-color 0.2s ease;
        }

        .progress-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: linear-gradient(90deg,
            rgba(59, 130, 246, 0.2) 0%,
            rgba(139, 92, 246, 0.2) 100%
          );
          pointer-events: none;
          transition: width 0.1s linear;
        }

        .scrubber-handle {
          position: absolute;
          top: 50%;
          width: 12px;
          height: 12px;
          background: var(--primary);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
          transition: transform 0.1s ease;
          z-index: 3;
        }

        .progress-container:hover .scrubber-handle {
          transform: translate(-50%, -50%) scale(1.2);
        }

        .audio-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .control-button {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--text-light);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .control-button:hover {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
          transform: scale(1.05);
        }

        .control-button svg {
          font-size: 14px;
        }

        /* Responsive */
        @media (max-width: 600px) {
          .audio-player-container {
            flex-wrap: wrap;
            gap: 12px;
          }

          .play-button {
            width: 44px;
            height: 44px;
          }

          .audio-content {
            flex: 1 1 100%;
            order: 2;
          }

          .audio-controls {
            order: 1;
          }

          .waveform-container {
            display: none;
          }

          .progress-container {
            height: 8px;
            border-radius: 4px;
          }

          .scrubber-handle {
            width: 16px;
            height: 16px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .pulse-ring {
            animation: none;
            display: none;
          }

          .play-button,
          .control-button,
          .scrubber-handle {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
}

export default AudioPlayer;
